import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { IoLogoPaypal } from "react-icons/io5";
import logo from "../../assets/IconMetuaa.png";
import profileAvatar from "../../assets/images/avatar.png";
import { apiUrl } from "../../Constants";
import WalletApi from "../../Services/Wallet";
import "../../Pages/DashboardClient/HomePage.css";
import "./HomePage.css";

const headerLinks = [
  { label: "Covoiturage", href: "/" },
  { label: "Bus", href: "/" },
  { label: "Train", href: "/" },
  { label: "Livraison", href: "/" },
  { label: "Courses", href: "/" },
  { label: "Offres", href: "/", badge: "Nouveau" },
];

function formatFCFA(amount) {
  const num = Number(amount) || 0;
  return num.toLocaleString("fr-FR").replace(/\u202f/g, " ");
}

function formatDateTime(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function LogoMark() {
  return <img src={logo} alt="Logo METUA'A" className="dbc-logo-image" />;
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4a5 5 0 0 0-5 5v2.5c0 .9-.3 2.1-.8 3l-.9 1.5h13.4l-.9-1.5c-.5-.9-.8-2.1-.8-3V9a5 5 0 0 0-5-5Z" />
      <path d="M9.5 19a2.5 2.5 0 0 0 5 0" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12l4 4 10-10" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 10.2v5.2" />
      <circle cx="12" cy="7.8" r="1" />
    </svg>
  );
}

function WalletIllustration() {
  return (
    <div className="paypal-approved-wallet-illustration" aria-hidden="true">
      <span className="paypal-approved-wallet-wallet">
        <span className="paypal-approved-wallet-wallet-band" />
        <span className="paypal-approved-wallet-wallet-slot" />
      </span>
      <span className="paypal-approved-wallet-coin" />
      <span className="paypal-approved-wallet-spark paypal-approved-wallet-spark-1" />
      <span className="paypal-approved-wallet-spark paypal-approved-wallet-spark-2" />
    </div>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l7 4v5c0 4.5-3 7.7-7 9-4-1.3-7-4.5-7-9V7l7-4Z" />
      <path d="M9.2 12.1l1.8 1.8 3.8-3.8" />
    </svg>
  );
}

function TopHeader() {
  const authUser = useSelector((state) => state?.AuthReducer?.user || {});

  const resolvedUser = {
    firstName: authUser?.firstName || authUser?.first_name || "",
    lastName: authUser?.lastName || authUser?.last_name || "",
    email: authUser?.email || "",
    phone: authUser?.phone || "",
    image_local: authUser?.image_local || "",
    avatar: authUser?.avatar_url || "",
  };

  const displayName = authUser?.name || `${resolvedUser.firstName} ${resolvedUser.lastName}`.trim() || "Utilisateur";
  const displaySubtitle = resolvedUser.email || resolvedUser.phone || "Voir le profil";

  return (
    <header className="dbc-nav-shell">
      <div className="dbc-container dbc-nav-container">
        <div className="dbc-nav">
          <Link to="/" className="dbc-logo" aria-label="METUA'A">
            <LogoMark />
            <span>METUA&apos;A</span>
          </Link>

          <nav className="dbc-main-links" aria-label="Navigation principale">
            {headerLinks.map((item) => (
              <Link key={item.label} to={item.href} className="dbc-main-link">
                <span>{item.label}</span>
                {item.badge ? <span className="dbc-main-badge">{item.badge}</span> : null}
              </Link>
            ))}
          </nav>

          <div className="dbc-right">
            <span className="dbc-flag" aria-label="France">
              <i />
              <i />
              <i />
            </span>

            <button type="button" className="dbc-notification-btn" aria-label="Notifications">
              <BellIcon />
              <span className="dbc-notification-badge">3</span>
            </button>

            <div className="dbc-profile-btn" aria-label="Profil utilisateur">
              <span className="dbc-profile-avatar" aria-hidden="true">
                <img
                  src={resolvedUser.image_local ? `${apiUrl}${resolvedUser.image_local}` : resolvedUser.avatar || profileAvatar}
                  alt=""
                  aria-hidden="true"
                />
              </span>
              <span className="dbc-profile-copy">
                <strong>{displayName}</strong>
                <small>{displaySubtitle}</small>
              </span>
              <ChevronDownIcon />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

function PaypalBadge() {
  return <IoLogoPaypal aria-hidden="true" focusable="false" />;
}

export default function PaypalApproved() {
  const navigate = useNavigate();
  const location = useLocation();
  const resolvedToken = String(
    useSelector((state) => state?.AuthReducer?.lewooPayToken || localStorage.getItem("lewooPayToken") || "")
  ).trim();
  const searchParams = new URLSearchParams(location.search);
  const orderId = searchParams.get("token") || searchParams.get("orderId") || searchParams.get("paypal_order_id") || "";
  const queryAmount = Number(searchParams.get("amount")) || 25000;
  const queryCurrency = searchParams.get("currency") || "FCFA";
  const fromWallet = searchParams.get("from") || "wallet-client";
  const VALID_WALLETS = ["wallet-client", "wallet-driver", "wallet-entreprise"];
  const walletRoute = VALID_WALLETS.includes(fromWallet) ? `/${fromWallet}` : "/wallet-client";

  const captureKeyRef = useRef("");
  const [isCapturing, setIsCapturing] = useState(Boolean(orderId));
  const [captureError, setCaptureError] = useState("");
  const [paymentData, setPaymentData] = useState({
    amount: queryAmount,
    currency: queryCurrency,
    paypal_order_id: orderId || "SEF56042UM779370V",
    paypal_capture_id: "",
    created_at: new Date().toISOString(),
    status: "Finalisation...",
  });

  const displayAmount = formatFCFA(paymentData.amount || 25000);
  const displayCurrency = paymentData.currency || "FCFA";
  const displayReference = paymentData.paypal_order_id || paymentData.paypal_capture_id || orderId || "SEF56042UM779370V";
  const displayDateTime = formatDateTime(paymentData.created_at);

  useEffect(() => {
    if (!orderId || !resolvedToken) {
      setIsCapturing(false);
      return undefined;
    }

    const captureKey = `${orderId}:${resolvedToken}`;
    if (captureKeyRef.current === captureKey) {
      return undefined;
    }

    captureKeyRef.current = captureKey;
    let isMounted = true;

    setIsCapturing(true);
    setCaptureError("");

    WalletApi.capturePaypalOrder(resolvedToken, orderId)
      .then((res) => {
        if (!isMounted) return;

        const data = res?.data?.data || {};
        setPaymentData((current) => ({
          ...current,
          amount: data.wallet_amount ?? data.credited_amount ?? current.amount ?? queryAmount,
          currency: data.wallet_currency || current.currency || queryCurrency,
          paypal_order_id: data.paypal_order_id || current.paypal_order_id || orderId,
          paypal_capture_id: data.paypal_capture_id || current.paypal_capture_id || "",
          created_at: data.created_at || current.created_at || new Date().toISOString(),
          status: data.status || current.status || "Finalisation...",
        }));
      })
      .catch((err) => {
        if (!isMounted) return;
        setCaptureError(err?.response?.data?.message || err?.message || "Impossible de finaliser le paiement.");
      })
      .finally(() => {
        if (!isMounted) return;
        setIsCapturing(false);
      });

    return () => {
      isMounted = false;
    };
  }, [orderId, queryAmount, queryCurrency, resolvedToken]);

  useEffect(() => {
    if (isCapturing) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      navigate(walletRoute, { replace: true });
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [isCapturing, navigate, walletRoute]);

  return (
    <div className="dbc-page paypal-approved-page">
      <TopHeader />

      <main className="paypal-approved-main">
        <div className="paypal-approved-shell">
          <section className="paypal-approved-hero" aria-label="Paiement approuve">
            <div className="paypal-approved-mark" aria-hidden="true">
              <span className="paypal-approved-mark-ring" />
              <span className="paypal-approved-mark-core">
                <CheckIcon />
              </span>
              <span className="paypal-approved-dot paypal-approved-dot-1" />
              <span className="paypal-approved-dot paypal-approved-dot-2" />
              <span className="paypal-approved-dot paypal-approved-dot-3" />
              <span className="paypal-approved-dot paypal-approved-dot-4" />
            </div>

            <h1>Paiement approuvé</h1>
            <p>
              Votre paiement a été effectué avec succès via PayPal.
              <br />
              Vous pouvez retourner sur Metua&apos;a, la recharge se termine automatiquement.
            </p>
          </section>

          <section className="paypal-approved-details" aria-label="Details du paiement">
            <div className="paypal-approved-row">
              <span>Montant rechargé</span>
              <strong className="paypal-approved-amount">
                {displayAmount} {displayCurrency}
              </strong>
            </div>

            <div className="paypal-approved-row">
              <span>Moyen de paiement</span>
              <strong className="paypal-approved-method">
                <span className="paypal-approved-method-icon">
                  <PaypalBadge />
                </span>
                PayPal (Sandbox)
              </strong>
            </div>

            <div className="paypal-approved-row">
              <span>Référence PayPal</span>
              <strong>{displayReference}</strong>
            </div>

            <div className="paypal-approved-row">
              <span>Statut</span>
              <strong className="paypal-approved-status">
                <span />
                {isCapturing ? "Finalisation..." : paymentData.status || "Finalisation..."}
              </strong>
            </div>

            <div className="paypal-approved-row">
              <span>Date et heure</span>
              <strong>{displayDateTime}</strong>
            </div>
          </section>

          {captureError ? (
            <section className="paypal-approved-error" role="alert">
              {captureError}
            </section>
          ) : null}

          <section className="paypal-approved-callout">
            <div className="paypal-approved-callout-icon" aria-hidden="true">
              <InfoIcon />
            </div>
            <div>
              <strong>L&apos;application va se fermer automatiquement</strong>
              <p>
                Vous allez être redirigé vers Metua&apos;a dans quelques secondes.
              </p>
            </div>
          </section>

          <div className="paypal-approved-manual">
            <p>L&apos;application ne s&apos;est pas ouverte automatiquement ?</p>
            <Link to={walletRoute} className="paypal-approved-manual-link">
              Ouvrir l&apos;application manuellement
            </Link>
          </div>

          <section className="paypal-approved-update-card" aria-label="Mise a jour du solde">
            <WalletIllustration />
            <div className="paypal-approved-update-copy">
              <strong>Solde en cours de mise à jour</strong>
              <p>
                Votre solde sera crédité automatiquement après confirmation finale du paiement.
              </p>
            </div>
          </section>

          <footer className="paypal-approved-footer">
            <div className="paypal-approved-footer-pill">
              <ShieldIcon />
              <span>Paiement sécurisé par PayPal (Sandbox)</span>
            </div>
            <p>Vos transactions sont sécurisées et chiffrées.</p>
          </footer>
        </div>
      </main>
    </div>
  );
}
